import {useEffect, useState, type ReactNode} from "react";
import {EditorContent, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import {Color} from "@tiptap/extension-color";
import {TextStyle} from "@tiptap/extension-text-style";
import {
  Bold,
  Code2,
  Italic,
  Link2,
  List,
  ListOrdered,
  Palette,
  Strikethrough,
  Underline as UnderlineIcon,
  Unlink
} from "lucide-react";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
};

const COLORS = [
  {value: "default", label: "Default", hex: "inherit"},
  {value: "#000000", label: "Black", hex: "#000000"},
  {value: "#ff4200", label: "Accent", hex: "#ff4200"},
  {value: "#606060", label: "Gray", hex: "#606060"},
  {value: "#34c9a2", label: "Green", hex: "#34c9a2"},
  {value: "#d32020", label: "Red", hex: "#d32020"},
  {value: "#2563eb", label: "Blue", hex: "#2563eb"}
];

const LINK_TARGETS = [
  {value: "_self", label: "Same window"},
  {value: "_blank", label: "New tab"}
];

function ToolbarSep() {
  return <span className="admin-html-editor__sep" aria-hidden="true" />;
}

function ToolbarGroup({children, className}: {children: ReactNode; className?: string}) {
  return <div className={cn("admin-html-editor__group", className)}>{children}</div>;
}

export function AdminHtmlEditor({value, onChange, placeholder = "Start writing…", className, minHeight = 220}: Props) {
  const [sourceMode, setSourceMode] = useState(false);
  const [source, setSource] = useState(value);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({url: "", target: "_self"});
  const [, setToolbarTick] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({heading: {levels: [1, 2, 3, 4, 5, 6]}}),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {rel: "noopener noreferrer"}
      })
    ],
    content: value || "",
    onUpdate: ({editor: ed}) => onChange(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "admin-html-editor__content prose prose-sm max-w-none focus:outline-none",
        style: `min-height:${minHeight}px`
      }
    }
  });

  useEffect(() => {
    if(!editor) return;
    const refresh = () => setToolbarTick((n) => n + 1);
    editor.on("selectionUpdate", refresh);
    editor.on("transaction", refresh);
    return () => {
      editor.off("selectionUpdate", refresh);
      editor.off("transaction", refresh);
    };
  }, [editor]);

  function activeColorValue() {
    if(!editor) return "default";
    const hex = editor.getAttributes("textStyle").color as string | undefined;
    if(!hex) return "default";
    return COLORS.find((c) => c.hex === hex)?.value || hex;
  }

  useEffect(() => {
    if(!editor || sourceMode) return;
    if(editor.getHTML() !== value) editor.commands.setContent(value || "", {emitUpdate: false});
  }, [value, editor, sourceMode]);

  useEffect(() => {
    if(sourceMode) setSource(value);
  }, [sourceMode, value]);

  function toggleSource() {
    if(sourceMode) {
      onChange(source);
      editor?.commands.setContent(source || "", {emitUpdate: false});
      setSourceMode(false);
      return;
    }
    setSource(editor?.getHTML() || value);
    setSourceMode(true);
  }

  function openLinkDialog() {
    if(!editor) return;
    const attrs = editor.getAttributes("link");
    setLinkForm({
      url: (attrs.href as string) || "",
      target: (attrs.target as string) || "_self"
    });
    setLinkOpen(true);
  }

  function applyLink() {
    if(!editor) return;
    const url = linkForm.url.trim();
    if(!url) return;
    const target = linkForm.target === "_blank" ? "_blank" : null;
    editor.chain().focus().extendMarkRange("link").setLink({href: url, target}).run();
    setLinkOpen(false);
  }

  function setTextColor(next: string) {
    if(!editor) return;
    if(next === "default") editor.chain().focus().unsetColor().run();
    else editor.chain().focus().setColor(next).run();
  }

  function toolBtn(active: boolean, onClick: () => void, label: string, icon: ReactNode) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={cn("admin-html-editor__btn", active && "isActive")}
        onClick={onClick}
        title={label}
        aria-label={label}
        aria-pressed={active}
      >
        {icon}
      </Button>
    );
  }

  const headingValue = [1, 2, 3, 4, 5, 6].find((l) => editor?.isActive("heading", {level: l}))?.toString() || "p";
  const isEmpty = !sourceMode && Boolean(editor?.isEmpty);

  return (
    <div className={cn("admin-html-editor", sourceMode && "isSource", className)}>
      <div className="admin-html-editor__toolbar" role="toolbar" aria-label="Formatting">
        {!sourceMode && editor ? (
          <>
            <ToolbarGroup>
              {toolBtn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "Bold", <Bold className="h-3.5 w-3.5" />)}
              {toolBtn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "Italic", <Italic className="h-3.5 w-3.5" />)}
              {toolBtn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "Underline", <UnderlineIcon className="h-3.5 w-3.5" />)}
              {toolBtn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), "Strike", <Strikethrough className="h-3.5 w-3.5" />)}
            </ToolbarGroup>
            <ToolbarSep />
            <ToolbarGroup>
              <Select
                value={headingValue}
                onValueChange={(v) => {
                  if(v === "p") editor.chain().focus().setParagraph().run();
                  else editor.chain().focus().toggleHeading({level: Number(v) as 1 | 2 | 3 | 4 | 5 | 6}).run();
                }}
              >
                <SelectTrigger className="admin-html-editor__select admin-html-editor__select--style" aria-label="Text style">
                  <SelectValue placeholder="Style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="p">Paragraph</SelectItem>
                  <SelectItem value="1">Heading 1</SelectItem>
                  <SelectItem value="2">Heading 2</SelectItem>
                  <SelectItem value="3">Heading 3</SelectItem>
                  <SelectItem value="4">Heading 4</SelectItem>
                  <SelectItem value="5">Heading 5</SelectItem>
                  <SelectItem value="6">Heading 6</SelectItem>
                </SelectContent>
              </Select>
              <Select value={activeColorValue()} onValueChange={setTextColor}>
                <SelectTrigger className="admin-html-editor__select admin-html-editor__select--color" title="Text color" aria-label="Text color">
                  <span className="admin-html-editor__colorTrig">
                    <Palette aria-hidden />
                    <span
                      className="admin-html-editor__swatch"
                      style={{backgroundColor: activeColorValue() === "default" ? "currentColor" : activeColorValue()}}
                    />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="admin-html-editor__colorOpt">
                        <span
                          className="admin-html-editor__swatch"
                          style={{backgroundColor: c.hex === "inherit" ? "transparent" : c.hex}}
                        />
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ToolbarGroup>
            <ToolbarSep />
            <ToolbarGroup>
              {toolBtn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "Bullet list", <List className="h-3.5 w-3.5" />)}
              {toolBtn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "Numbered list", <ListOrdered className="h-3.5 w-3.5" />)}
            </ToolbarGroup>
            <ToolbarSep />
            <ToolbarGroup>
              {toolBtn(editor.isActive("link"), openLinkDialog, "Insert link", <Link2 className="h-3.5 w-3.5" />)}
              {toolBtn(false, () => editor.chain().focus().unsetLink().run(), "Remove link", <Unlink className="h-3.5 w-3.5" />)}
            </ToolbarGroup>
          </>
        ) : (
          <span className="admin-html-editor__modeLabel">{sourceMode ? "HTML source" : "Loading editor…"}</span>
        )}
        <div className="admin-html-editor__toolbarEnd">
          {toolBtn(sourceMode, toggleSource, sourceMode ? "Visual editor" : "View source", <Code2 className="h-3.5 w-3.5" />)}
        </div>
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editor-link-url">URL</Label>
              <Input
                id="editor-link-url"
                value={linkForm.url}
                onChange={(e) => setLinkForm({...linkForm, url: e.target.value})}
                placeholder="https://"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editor-link-target">Target</Label>
              <Select value={linkForm.target} onValueChange={(target) => setLinkForm({...linkForm, target})}>
                <SelectTrigger id="editor-link-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LINK_TARGETS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button type="button" onClick={applyLink} disabled={!linkForm.url.trim()}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sourceMode ? (
        <Textarea
          className="admin-html-editor__source"
          style={{minHeight}}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder={placeholder}
          rows={12}
          spellCheck={false}
        />
      ) : (
        <div
          className={cn("admin-html-editor__surface", isEmpty && "isEmpty")}
          data-placeholder={placeholder}
        >
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}
